"use client"

import type React from "react"
import { useEffect, useState, useRef } from "react"
import { useCursorContext } from "./CursorProvider"
import type { MousePosition } from "../types"

interface CursorProps {
  zIndex?: number
  showOnTouch?: boolean
  trailLength?: number
}

// Velocity Verlet spring integrator — stable at variable frame rates
function stepSpring(
  pos: number,
  vel: number,
  target: number,
  stiffness: number,
  damping: number,
  mass: number,
  dt: number
): [number, number] {
  const force = -stiffness * (pos - target) - damping * vel
  const acc = force / mass
  const newVel = vel + acc * dt
  const newPos = pos + vel * dt + 0.5 * acc * dt * dt
  return [newPos, newVel]
}

export const Cursor: React.FC<CursorProps> = ({ zIndex = 9999, showOnTouch = false, trailLength = 10 }) => {
  const { cursorState, mousePosition } = useCursorContext()
  const [isVisible, setIsVisible] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)

  const outerRef = useRef<HTMLDivElement>(null)
  const positionHistoryRef = useRef<MousePosition[]>([])

  // All mutable animation state lives in refs — never causes re-renders
  const springRef = useRef({ x: 0, y: 0, vx: 0, vy: 0 })
  const targetRef = useRef({ x: 0, y: 0 })
  const rafRef = useRef<number | null>(null)
  const lastTimeRef = useRef<number | null>(null)
  const springConfigRef = useRef({ stiffness: 800, damping: 80, mass: 1 })
  const reducedMotionRef = useRef(false)

  // Keep spring config current without restarting the loop
  useEffect(() => {
    springConfigRef.current = {
      stiffness: cursorState.transition?.stiffness ?? 800,
      damping: cursorState.transition?.damping ?? 80,
      mass: cursorState.transition?.mass ?? 1,
    }
  }, [cursorState.transition])

  // Direct mousemove listener — bypasses React state entirely, minimum possible lag.
  // targetRef is updated synchronously on every mouse event; the RAF loop reads it
  // on the very next frame. No re-render cycle in the critical path.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      targetRef.current = { x: e.clientX, y: e.clientY }
      positionHistoryRef.current = [
        { x: e.clientX, y: e.clientY },
        ...positionHistoryRef.current.slice(0, trailLength - 1),
      ]
    }
    window.addEventListener("mousemove", onMove, { passive: true })
    return () => window.removeEventListener("mousemove", onMove)
  }, [trailLength])

  // Single RAF loop — starts once, reads only from refs, writes only to DOM
  useEffect(() => {
    const animate = (time: number) => {
      if (lastTimeRef.current !== null) {
        if (reducedMotionRef.current) {
          const { x, y } = targetRef.current
          springRef.current = { x, y, vx: 0, vy: 0 }
          if (outerRef.current) outerRef.current.style.transform = `translate(${x}px, ${y}px)`
        } else {
          const dt = Math.min((time - lastTimeRef.current) / 1000, 0.064)
          const { stiffness, damping, mass } = springConfigRef.current
          const { x, y, vx, vy } = springRef.current
          const { x: tx, y: ty } = targetRef.current

          const [nx, nvx] = stepSpring(x, vx, tx, stiffness, damping, mass, dt)
          const [ny, nvy] = stepSpring(y, vy, ty, stiffness, damping, mass, dt)

          springRef.current = { x: nx, y: ny, vx: nvx, vy: nvy }
          if (outerRef.current) outerRef.current.style.transform = `translate(${nx}px, ${ny}px)`
        }
      }
      lastTimeRef.current = time
      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // Device + accessibility detection
  useEffect(() => {
    // Use pointer media query — coarse means touch/stylus, fine means mouse
    const touchMQ = window.matchMedia("(pointer: coarse)")
    setIsTouchDevice(touchMQ.matches)

    const motionMQ = window.matchMedia("(prefers-reduced-motion: reduce)")
    reducedMotionRef.current = motionMQ.matches
    setReducedMotion(motionMQ.matches)

    const onMotionChange = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches
      setReducedMotion(e.matches)
    }
    motionMQ.addEventListener("change", onMotionChange)

    // Prevent cursor flash on page load
    const timer = setTimeout(() => setIsVisible(true), 50)

    return () => {
      motionMQ.removeEventListener("change", onMotionChange)
      clearTimeout(timer)
    }
  }, [])

  if (isTouchDevice && !showOnTouch) return null

  const {
    width = 24,
    height = 24,
    scale = 1,
    shape = "circle",
    color = "rgba(255, 255, 255, 0.8)",
    opacity = 1,
    borderColor = "rgba(0, 0, 0, 0)",
    borderWidth = 0,
    borderStyle = "solid",
    mixBlendMode = "normal",
    label,
    fontSize = "12px",
    fontWeight = "bold",
    labelColor = "#000",
    customElement,
    innerElements,
    borderRadius: customBorderRadius,
  } = cursorState

  const borderRadius =
    shape === "circle" ? "50%" : shape === "square" ? `${customBorderRadius ?? 0}px` : "0"

  const bgColor = shape === "custom" ? "transparent" : color

  const shapeTransition = reducedMotion
    ? "none"
    : "width 0.2s ease, height 0.2s ease, border-radius 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, opacity 0.2s ease, transform 0.15s ease"

  return (
    <div
      ref={outerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        left: 0,
        top: 0,
        zIndex,
        pointerEvents: "none",
        opacity: isVisible ? 1 : 0,
        willChange: "transform",
        mixBlendMode,
      }}
    >
      <div
        style={{
          width,
          height,
          borderRadius,
          backgroundColor: bgColor,
          opacity,
          borderColor,
          borderWidth,
          borderStyle,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          transform: `translate(-50%, -50%) scale(${scale})`,
          transition: shapeTransition,
          willChange: "width, height, background-color, transform",
        }}
      >
        {shape === "custom" && customElement}

        {label && (
          <div
            style={{
              fontSize,
              fontWeight,
              color: labelColor,
              textAlign: "center",
              userSelect: "none",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {label}
          </div>
        )}

        {innerElements?.map((item, index) => {
          const delayedPos =
            positionHistoryRef.current[Math.min(index + 1, positionHistoryRef.current.length - 1)] ||
            mousePosition
          const offsetX = mousePosition.x - delayedPos.x
          const offsetY = mousePosition.y - delayedPos.y

          return (
            <div
              key={`inner-${index}`}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(calc(-50% + ${-offsetX * 0.5}px), calc(-50% + ${-offsetY * 0.5}px))`,
                transition: reducedMotion ? "none" : "transform 0.12s ease",
              }}
            >
              {item.element}
            </div>
          )
        })}
      </div>
    </div>
  )
}
